import { cn } from '@/lib/utils';

interface MonthlyPerformance {
  month: string;
  enviados: number;
  pagos: number;
  taxa: number;
  valorRecuperado: number;
}

// Mock data - será substituído por dados reais
const MOCK_PERFORMANCE: MonthlyPerformance[] = [
  { month: 'Janeiro', enviados: 1250, pagos: 980, taxa: 78.4, valorRecuperado: 145200 },
  { month: 'Fevereiro', enviados: 1180, pagos: 965, taxa: 81.8, valorRecuperado: 138500 },
  { month: 'Março', enviados: 1320, pagos: 1045, taxa: 79.2, valorRecuperado: 162300 },
  { month: 'Abril', enviados: 1420, pagos: 1210, taxa: 85.2, valorRecuperado: 185400 },
  { month: 'Maio', enviados: 1380, pagos: 1165, taxa: 84.4, valorRecuperado: 172800 },
  { month: 'Junho', enviados: 1450, pagos: 1268, taxa: 87.4, valorRecuperado: 198250 },
];

function formatMoney(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function getRateColor(rate: number) {
  if (rate >= 85) return 'text-[hsl(var(--accent-600))]';
  if (rate >= 75) return 'text-[hsl(var(--basic-600))]';
  if (rate >= 60) return 'text-[hsl(40,95%,40%)]';
  return 'text-[hsl(var(--careful-600))]';
}

export function MonthlyPerformanceTable() {
  const totalRecuperado = MOCK_PERFORMANCE.reduce((sum, p) => sum + p.valorRecuperado, 0);
  const avgTaxa = (MOCK_PERFORMANCE.reduce((sum, p) => sum + p.taxa, 0) / MOCK_PERFORMANCE.length).toFixed(1);

  return (
    <div className="rounded-2xl border bg-card overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="px-5 py-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Performance Mensal de Recuperação</h3>
            <p className="text-xs text-muted-foreground">Últimos 6 meses</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Total Recuperado</p>
            <p className="text-lg font-bold text-[hsl(var(--accent-600))]">{formatMoney(totalRecuperado)}</p>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="text-left py-3 px-5 font-semibold">Mês</th>
              <th className="text-center py-3 px-3 font-semibold">Enviados</th>
              <th className="text-center py-3 px-3 font-semibold">Pagos</th>
              <th className="text-center py-3 px-3 font-semibold">Taxa</th>
              <th className="text-right py-3 px-5 font-semibold">Valor Recuperado</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_PERFORMANCE.map((perf, index) => (
              <tr 
                key={perf.month}
                className={cn(
                  'border-b border-border/50 hover:bg-muted/30 transition-colors',
                  index === MOCK_PERFORMANCE.length - 1 && 'border-b-0'
                )}
              >
                <td className="py-3 px-5">
                  <span className="text-foreground font-medium">{perf.month}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="text-foreground">{perf.enviados.toLocaleString('pt-BR')}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="text-foreground">{perf.pagos.toLocaleString('pt-BR')}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className={cn('font-bold', getRateColor(perf.taxa))}>
                    {perf.taxa}%
                  </span>
                </td>
                <td className="py-3 px-5 text-right">
                  <span className="font-semibold text-foreground">
                    {formatMoney(perf.valorRecuperado)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-muted/50">
              <td className="py-3 px-5 font-semibold text-foreground">Média/Total</td>
              <td className="py-3 px-3 text-center font-medium text-muted-foreground">-</td>
              <td className="py-3 px-3 text-center font-medium text-muted-foreground">-</td>
              <td className="py-3 px-3 text-center">
                <span className={cn('font-bold', getRateColor(Number(avgTaxa)))}>
                  {avgTaxa}%
                </span>
              </td>
              <td className="py-3 px-5 text-right">
                <span className="font-bold text-[hsl(var(--accent-600))]">
                  {formatMoney(totalRecuperado)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
